"use client";

import * as React from "react";
import { CADENCE } from "../../../data/viz";
import { useSize } from "../../../hooks";
import VizFrame from "./VizFrame";

const DAY_MS = 86_400_000;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Calendar heatmap.
 *
 * Position carries the date and colour carries volume, which means streaks and
 * dry spells read without an axis. A line chart of the same series would show
 * the total and hide the rhythm, which is the only interesting part.
 */
export default function Cadence() {
  const [ref, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = React.useState<{
    d: string;
    n: number;
    x: number;
    y: number;
  } | null>(null);

  const { weeks, byKey, total, max, monthTicks, months, monthMax } =
    React.useMemo(() => {
      const byKey = new Map(CADENCE.map((c) => [c.d, c.n]));
      const first = new Date(`${CADENCE[0].d}T00:00:00Z`);
      const last = new Date(`${CADENCE[CADENCE.length - 1].d}T00:00:00Z`);

      // Start on the Sunday on or before the first commit.
      const start = new Date(first.getTime() - first.getUTCDay() * DAY_MS);
      const weeks: string[][] = [];
      const monthTicks: { week: number; label: string }[] = [];

      let cursor = start.getTime();
      let seenMonth = -1;
      while (cursor <= last.getTime() + 6 * DAY_MS) {
        const col: string[] = [];
        for (let d = 0; d < 7; d++) {
          const day = new Date(cursor + d * DAY_MS);
          col.push(day.toISOString().slice(0, 10));
        }
        const firstOfCol = new Date(cursor);
        if (firstOfCol.getUTCMonth() !== seenMonth) {
          seenMonth = firstOfCol.getUTCMonth();
          monthTicks.push({ week: weeks.length, label: MONTHS[seenMonth] });
        }
        weeks.push(col);
        cursor += 7 * DAY_MS;
      }

      // Commits per calendar month, keyed to the week columns each month
      // spans, so the bars below line up under the heatmap.
      const months = monthTicks.map((m, i) => {
        const from = m.week;
        const to =
          i + 1 < monthTicks.length ? monthTicks[i + 1].week : weeks.length;
        let n = 0;
        for (let wk = from; wk < to; wk++)
          for (const day of weeks[wk]) n += byKey.get(day) ?? 0;
        return { ...m, from, to, n };
      });

      return {
        weeks,
        byKey,
        total: CADENCE.reduce((s, c) => s + c.n, 0),
        max: Math.max(...CADENCE.map((c) => c.n)),
        monthTicks,
        months,
        monthMax: Math.max(...months.map((m) => m.n)),
      };
    }, []);

  const pad = { top: 16, left: 4, right: 4, bottom: 6 };
  const avail = Math.max(size.width - 24 - pad.left - pad.right, 200);
  const cell = Math.max(5, Math.min(14, Math.floor(avail / weeks.length) - 2));
  const gap = 2;
  const w = weeks.length * (cell + gap) + pad.left + pad.right;
  const gridH = 7 * (cell + gap);
  // The monthly strip: the heatmap shows rhythm, this shows volume, and the
  // two share an x-axis so a busy month reads as a dark band over a tall bar.
  const barTop = pad.top + gridH + 22;
  const barH = 56;
  const h = barTop + barH + 16 + pad.bottom;

  return (
    <VizFrame
      title="A year of solo development"
      meta={`${total.toLocaleString()} commits · ${CADENCE.length} active days`}
    >
      <div ref={ref} className="relative p-3">
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label={`Calendar heatmap of ${total} commits across ${CADENCE.length} active days, from August 2025 to August 2026.`}
        >
          {monthTicks.map((m, i) =>
            // Skip a label that would collide with the previous one.
            i === 0 || m.week - monthTicks[i - 1].week > 2 ? (
              <text
                key={`${m.label}-${m.week}`}
                x={pad.left + m.week * (cell + gap)}
                y={10}
                className="fill-[rgb(var(--faint))] font-mono text-[9px]"
              >
                {m.label}
              </text>
            ) : null,
          )}

          {weeks.map((col, wi) =>
            col.map((day, di) => {
              const n = byKey.get(day) ?? 0;
              const t = n === 0 ? 0 : 0.18 + (n / max) * 0.82;
              return (
                <rect
                  key={day}
                  x={pad.left + wi * (cell + gap)}
                  y={pad.top + di * (cell + gap)}
                  width={cell}
                  height={cell}
                  rx={2}
                  fill={
                    n === 0 ? "rgb(var(--line) / 0.1)" : "rgb(var(--accent))"
                  }
                  opacity={n === 0 ? 1 : t}
                  onMouseEnter={(e) =>
                    setHover({
                      d: day,
                      n,
                      x: e.currentTarget.getBoundingClientRect().left,
                      y: e.currentTarget.getBoundingClientRect().top,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              );
            }),
          )}

          <g aria-hidden>
            <text
              x={pad.left}
              y={barTop - 8}
              className="fill-[rgb(var(--faint))] font-mono text-[9px]"
            >
              commits per month
            </text>
            {months.map((m) => {
              const x = pad.left + m.from * (cell + gap);
              const width = Math.max(1, (m.to - m.from) * (cell + gap) - gap);
              const bh = monthMax ? Math.round((m.n / monthMax) * barH) : 0;
              return (
                <g key={`bar-${m.label}-${m.week}`}>
                  <rect
                    x={x}
                    y={barTop}
                    width={width}
                    height={barH}
                    rx={2}
                    fill="rgb(var(--line) / 0.06)"
                  />
                  <rect
                    x={x}
                    y={barTop + barH - bh}
                    width={width}
                    height={bh}
                    rx={2}
                    fill="rgb(var(--accent))"
                    opacity={0.55}
                  />
                  {width >= 26 && (
                    <text
                      x={x + width / 2}
                      y={barTop + barH + 11}
                      textAnchor="middle"
                      className="fill-[rgb(var(--faint))] font-mono text-[9px]"
                    >
                      {m.n}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        <div className="mt-2 flex items-center gap-2">
          <span className="font-mono text-2xs text-faint">less</span>
          {[0, 0.3, 0.55, 0.8, 1].map((t) => (
            <span
              key={t}
              className="h-2.5 w-2.5 rounded-[2px]"
              style={{
                background:
                  t === 0
                    ? "rgb(var(--line) / 0.1)"
                    : `rgb(var(--accent) / ${0.18 + t * 0.82})`,
              }}
            />
          ))}
          <span className="font-mono text-2xs text-faint">
            more · peak {max} in a day
          </span>
        </div>

        {hover && hover.n > 0 && (
          <div
            className="pointer-events-none fixed z-30 -translate-x-1/2 -translate-y-full rounded-md border bg-surface px-2 py-1 font-mono text-2xs text-muted shadow-lg"
            style={{ left: hover.x + 6, top: hover.y - 6 }}
          >
            {hover.n} commit{hover.n === 1 ? "" : "s"} · {hover.d}
          </div>
        )}
      </div>
    </VizFrame>
  );
}
